/*----------------------------------
- PRODUCT PAGE ROUTES CATALOG (SSOT)
----------------------------------*/

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const PRODUCT_PAGE_ROOT = '/home';
const LEGACY_PRODUCT_PAGE_PREFIX = '/app';

const normalizeAbsolutePath = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '/';

    const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')) {
        return withLeadingSlash.replace(/\/+$/, '');
    }

    return withLeadingSlash;
};

export const withProductPagePrefix = (path: string): string => {
    const normalized = normalizeAbsolutePath(path);

    if (normalized === LEGACY_PRODUCT_PAGE_PREFIX) {
        return PRODUCT_PAGE_ROOT;
    }

    if (normalized.startsWith(`${LEGACY_PRODUCT_PAGE_PREFIX}/`)) {
        return normalizeAbsolutePath(normalized.slice(LEGACY_PRODUCT_PAGE_PREFIX.length));
    }

    if (normalized === '/') {
        return PRODUCT_PAGE_ROOT;
    }

    return normalized;
};

export const PRODUCT_PAGE_ROUTE_PREFIXES = {
    founder: withProductPagePrefix('/founder'),
    godmode: withProductPagePrefix('/godmode'),
    /** Projects merged into Radars (ADR-0051): Product redirects `/projects/*` to `/radars/*`. */
    projects: withProductPagePrefix('/projects'),
} as const;

/**
 * Paths Product used to serve. They exist here so the legacy redirect can still
 * recognise an old link and forward it somewhere useful, and they are kept out
 * of `PRODUCT_PAGE_ROUTES` on purpose: nothing may link to them as a destination.
 */
export const RETIRED_PRODUCT_PAGE_PATHS = {
    /** Retired 2026-07-26. Its picks now arrive in the Home feed. */
    opportunities: withProductPagePrefix('/opportunities'),
    /** Retired 2026-08-28 with the AI Connector (MCP). Nothing replaces it. */
    mcpSetup: withProductPagePrefix('/mcp/setup'),
    /** Retired 2026-10-12 with Portfolio (ADR-0081). Nothing replaces it. */
    portfolio: withProductPagePrefix('/portfolio'),
} as const;

/**
 * Product's team invitation (`/invite/team/<token>`, Product's `PRODUCT_PUBLIC_ROUTES.teamInvite`): outside the
 * workspace and its access pin, so a hand-off may land on it (the invite's Switch account, ADR-0109 decision 26).
 */
export const PRODUCT_TEAM_INVITE_PATH = '/invite/team';

export const PRODUCT_PAGE_ROUTES = {
    root: PRODUCT_PAGE_ROOT,
    domains: withProductPagePrefix('/domains'),
    radars: withProductPagePrefix('/radars'),
    watchlists: withProductPagePrefix('/watchlists'),
    api: withProductPagePrefix('/api'),
    home: withProductPagePrefix('/home'),
    /** Market > Calendar: the public agenda plus the member's own dates (the agenda's "Get my dates"). */
    calendar: withProductPagePrefix('/calendar'),
    /** Market > TLDs: every root TLD with its figures (the TLD board's "See every figure"). */
    tlds: withProductPagePrefix('/tlds'),
    /** Market > Applications: ICANN's 2026 round with the member's follows (the applications board's Follow). */
    applications: withProductPagePrefix('/applications'),
    /** Market > Sales: every sale with its marketplace, the lanes and alerts (the sales page's walls, ADR-0067). */
    sales: withProductPagePrefix('/sales'),
    /** Market > Auctions: every closing name, radars, results and marks (the auctions page's walls, ADR-0076). */
    auctions: withProductPagePrefix('/auctions'),
    /** The saved brief: onboarding, the settings modal and the paid-access pin's page (ADR-0083); the handoff follows its `returnTo`. */
    brief: withProductPagePrefix('/brief'),
    /**
     * The `/founder/*` tree and the Brand Pack were removed from Product. Only
     * the prefix survives, in the legacy redirect that forwards those old paths
     * to Home. Do not add page keys for routes Product does not serve: they read
     * as destinations and cannot be reached.
     */
} as const;
